import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import {
  requestOtp,
  verifyOtp,
  register,
  getConsent,
  refreshToken
} from 'services/authService';
import {
  OtpRequestData,
  OtpVerifyData,
  RegistrationFormData,
  AuthResponse
} from '../../types';
import { STORAGE_KEYS } from '../../constants';
import { useAuth } from '../../context/AuthContext';
import { useChatWebSocket } from '../../../../contexts/ChatWebSocketContext';
import storesService, { getDefaultStoreSummaryDateFilters } from 'features/Stores/services/StoresService';
import { getFirstOperatorStoreId, getManagerStores, isOperatorOnlyUser } from 'utils/storeAccess';

// OTP Request Mutation
export const useRequestOtpMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OtpRequestData) => requestOtp(data.identifier, {
      authorizeOtp: data.authorize_otp,
      call: data.call,
    }),
    onSuccess: (response: any) => {
      if (response.success) {
        toast.success(response.data.message || 'OTP sent successfully');
        queryClient.invalidateQueries({ queryKey: ['auth'] });
      } else {
        toast.error(response.error);
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || 'Failed to send OTP';
      toast.error(errorMessage);
    },
  });
};

// OTP Verification Mutation
export const useVerifyOtpMutation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { clearChatSession } = useChatWebSocket();

  return useMutation({
    mutationFn: async (data: OtpVerifyData) => {
      const response = await verifyOtp(data.identifier, data.otp);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'OTP verification failed');
      }
      return { data: response.data };
    },
    onSuccess: async (response: { data: AuthResponse }) => {
      const { access_token, refresh_token } = response.data;

      // Clear chat session on successful login
      clearChatSession();

      // Store tokens
      localStorage.setItem(STORAGE_KEYS.TOKEN, access_token);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh_token);

      // Smart redirect based on user's stores. Use the stores-only endpoint so
      // operator-only users do not hit owner/manager reconciliation endpoints.
      try {
        const stores = await storesService.getStoresSummaryStoresOnly();
        const { startDate, endDate } = getDefaultStoreSummaryDateFilters();
        queryClient.setQueryData(
          ['stores', 'storesOnlySummary', startDate, endDate],
          stores
        );

        if (stores.length === 0) {
          navigate('/add_store');
        } else if (isOperatorOnlyUser(stores)) {
          const auditStoreId = getFirstOperatorStoreId(stores);
          navigate(auditStoreId ? `/store/${auditStoreId}/audit-scan` : '/add_store');
        } else if (stores.length === 1) {
          navigate(`/store/${stores[0].store.id}`);
        } else {
          const managerStores = getManagerStores(stores);
          navigate(managerStores.length === 1 ? `/store/${managerStores[0].store.id}` : '/stores');
        }
      } catch (storesError) {
        console.error('Error fetching stores:', storesError);
        // Fallback to stores page if API fails
        navigate('/stores');
      }

      toast.success('Login successful');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || 'Invalid OTP';
      toast.error(errorMessage);
    },
  });
};

// Registration Mutation
export const useRegistrationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RegistrationFormData) => register(data),
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['auth'] });
        toast.success('Registration successful');
      }
      else {
        toast.error(response.error.detail);
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || 'Registration failed';
      toast.error(errorMessage);
    },
  });
};

// Consent Check Mutation
export const useConsentMutation = () => {
  return useMutation({
    mutationFn: (phoneNumber: string) => getConsent(phoneNumber),
    onError: (error: any) => {
      console.error('Consent check failed:', error);
    },
  });
};

// Refresh Token Mutation
export const useRefreshTokenMutation = () => {
  const queryClient = useQueryClient();
  const { login, logout } = useAuth();

  return useMutation({
    mutationFn: async (refreshTokenValue: string) => {
      const response = await refreshToken(refreshTokenValue);
      if (!response.success || !response.data) {
        const refreshError = new Error('Token refresh failed') as any;
        refreshError.status = response.status;
        refreshError.message = typeof response.error === 'string' ? response.error : 'Token refresh failed';
        throw refreshError;
      }
      return { data: response.data };
    },
    onSuccess: (response: { data: AuthResponse }) => {
      const { access_token, refresh_token } = response.data;

      // Store new tokens
      localStorage.setItem(STORAGE_KEYS.TOKEN, access_token);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh_token);

      // Update auth context
      login(access_token, refresh_token);

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['loggedinUser'] });
    },
    onError: (error: any) => {
      const refreshStatus = error?.status ?? error?.response?.status;
      if (refreshStatus === 401) {
        logout();
        toast.error('Session expired. Please login again.');
        return;
      }
      toast.error('Authentication refresh failed. Please try again.');
    },
  });
};

// Logout Mutation
export const useLogoutMutation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { clearChatSession } = useChatWebSocket();

  return useMutation({
    mutationFn: async () => {
      // Clear chat session
      clearChatSession();

      // Clear all auth data
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);

      // Update auth context
      logout();

      // Clear all queries
      queryClient.clear();

      // Navigate to login
      navigate('/');

      return Promise.resolve();
    },
    onSuccess: () => {
      toast.success('Logged out successfully');
    },
    onError: () => {
      toast.error('Logout failed');
    },
  });
}; 
